// Coklu Etsy magazasi yonetimi: listeleme, yeni baglama, etiket/renk,
// pasife alma, varsayilan yapma, token tazeleme, silme.
//
// POD is modelinde ayni kullanici farkli nislerde birden fazla magaza yonetir;
// tasarim havuzu ortaktir, magazalar ayrilir.

import { useEffect, useState } from "react";
import {
  Store, Link2, Loader2, RefreshCw, Trash2, AlertCircle, ShieldCheck,
  Plus, Star, Power, ExternalLink, Info,
} from "lucide-react";
import { clsx } from "clsx";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { useShopStore, type EtsyShopDto } from "../../store/shop.store";

const COLORS = ["#6366f1", "#f97316", "#10b981", "#ec4899", "#0ea5e9", "#eab308", "#8b5cf6", "#ef4444"];

// Magazanin dikeyi: hangi urun grubunu satiyor. Yeni listede varsayilan urun
// tipini ve gorsel uretim mantigini belirler.
const VERTICALS: Array<{ key: string; label: string }> = [
  { key: "apparel", label: "Tekstil (tişört, sweat, hoodie)" },
  { key: "giftware", label: "Hediyelik (kupa, paspas, süsleme)" },
  { key: "jewelry", label: "Takı (kolye, yüzük, bileklik)" },
];

const PLATFORM_LABEL: Record<string, string> = {
  etsy: "Etsy", trendyol: "Trendyol", amazon: "Amazon",
};

async function api(path: string, init?: RequestInit) {
  const res = await fetch(`${getBackendUrl()}/api/etsy/shops${path}`, {
    ...init,
    headers: authHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || "İşlem başarısız");
  return body.data;
}

export function EtsyShopsCard() {
  const { shops: allShops, activeShopId, loadShops, setActiveShop } = useShopStore();
  // Bu kart YALNIZCA Etsy magazalarini yonetir (OAuth, token tazeleme, keystring).
  // Trendyol'un baglama akisi ve alanlari tamamen farkli; kendi kartinda durur.
  const shops = allShops.filter((s) => s.platform === "etsy");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newSecret, setNewSecret] = useState("");

  useEffect(() => { loadShops(); }, [loadShops]);

  async function run(id: string | null, fn: () => Promise<unknown>) {
    setBusyId(id);
    setError(null);
    try {
      await fn();
      await loadShops();
    } catch (e) {
      setError(e instanceof Error ? e.message : "İşlem başarısız");
    } finally {
      setBusyId(null);
    }
  }

  // Yeni magaza baglama / yeniden baglama — OAuth'a yonlendirir.
  async function connect(opts?: { apiKey?: string; sharedSecret?: string; shopId?: string }) {
    setError(null);
    try {
      const params = new URLSearchParams();
      if (opts?.apiKey) params.set("apiKey", opts.apiKey);
      if (opts?.sharedSecret) params.set("sharedSecret", opts.sharedSecret);
      if (opts?.shopId) params.set("shopId", opts.shopId);

      const res = await fetch(
        `${getBackendUrl()}/api/etsy/oauth/connect${params.toString() ? `?${params}` : ""}`,
        { headers: authHeaders() }
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Bağlantı başlatılamadı");
      window.location.href = body.data.url;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Bağlantı hatası");
    }
  }

  return (
    <div className="rounded-xl border border-orange-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-orange-50">
          <Store className="h-5 w-5 text-orange-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-base font-semibold text-gray-900">Etsy Mağazaları</h2>
          <p className="text-sm text-gray-500">
            Birden fazla mağaza bağlayabilirsiniz. Tasarım havuzu ortaktır; siparişler,
            ürünler ve envanter seçili mağazaya göre gösterilir.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-600"
        >
          <Plus className="h-3.5 w-3.5" /> Mağaza ekle
        </button>
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Yeni magaza formu */}
      {showAdd && (
        <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50/50 p-4">
          <p className="mb-2 text-sm font-medium text-gray-800">Yeni mağaza bağla</p>

          <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-800">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Tarayıcınız şu an başka bir Etsy hesabına girişliyse Etsy aynı mağazayı tekrar
              bağlar. Farklı bir mağaza için önce Etsy'den çıkış yapın veya <b>gizli sekme</b> kullanın.
            </span>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                API Key (Keystring) <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder="Bu mağazanın Etsy uygulaması keystring'i"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Shared Secret</label>
              <input
                type="password"
                value={newSecret}
                onChange={(e) => setNewSecret(e.target.value)}
                placeholder="Opsiyonel"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <p className="mt-1 text-[11px] text-gray-500">
            <b>Her mağaza kendi Etsy uygulamasıyla bağlanır</b> — ortak/varsayılan anahtar
            yoktur. Böylece mağazalar birbirinin günlük API kotasını tüketmez ve bir
            anahtarın iptali diğerlerini etkilemez. Uygulamanın callback URL listesine
            <code className="mx-1 rounded bg-white px-1">{window.location.origin}/settings/etsy-callback</code>
            eklemeyi unutmayin.
          </p>

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              disabled={!newKey.trim()}
              title={!newKey.trim() ? "Önce bu mağazanın API Key'ini girin" : undefined}
              onClick={() => connect({ apiKey: newKey.trim(), sharedSecret: newSecret.trim() || undefined })}
              className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-600 disabled:opacity-40"
            >
              <Link2 className="h-3.5 w-3.5" /> Etsy'ye bağlan
            </button>
            <button
              type="button"
              onClick={() => { setShowAdd(false); setNewKey(""); setNewSecret(""); }}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
            >
              İptal
            </button>
          </div>
        </div>
      )}

      {/* Magaza listesi */}
      {shops.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 p-6 text-center">
          <Store className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          <p className="text-sm text-gray-500">Henüz bağlı mağaza yok</p>
          <p className="mt-0.5 text-xs text-gray-400">
            "Mağaza ekle" ile bağlayın — her mağaza kendi Etsy API anahtarını kullanır
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {shops.map((shop) => (
            <ShopRow
              key={shop.id}
              shop={shop}
              isSelected={shop.id === activeShopId}
              busy={busyId === shop.id}
              onSelect={() => setActiveShop(shop.id)}
              onPatch={(data) => run(shop.id, () => api(`/${shop.id}`, { method: "PATCH", body: JSON.stringify(data) }))}
              onRefresh={() => run(shop.id, () => api(`/${shop.id}/refresh`, { method: "POST" }))}
              onDelete={() => {
                if (!confirm(`"${shop.shopName}" bağlantısı kaldırılsın mı? Tasarımlarınız ve taslaklarınız silinmez.`)) return;
                run(shop.id, () => api(`/${shop.id}`, { method: "DELETE" }));
              }}
              onReconnect={() => connect({ shopId: shop.id })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ShopRow({
  shop, isSelected, busy, onSelect, onPatch, onRefresh, onDelete, onReconnect,
}: {
  shop: EtsyShopDto;
  isSelected: boolean;
  busy: boolean;
  onSelect: () => void;
  onPatch: (data: Record<string, unknown>) => void;
  onRefresh: () => void;
  onDelete: () => void;
  onReconnect: () => void;
}) {
  const [editingLabel, setEditingLabel] = useState(false);
  const [label, setLabel] = useState(shop.label || "");

  return (
    <div
      className={clsx(
        "rounded-lg border p-3 transition-colors",
        !shop.isActive ? "border-gray-200 bg-gray-50 opacity-70"
          : isSelected ? "border-indigo-300 bg-indigo-50/40"
          : "border-gray-200 bg-white"
      )}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => onPatch({ color: COLORS[(COLORS.indexOf(shop.color || "") + 1) % COLORS.length] })}
          title="Rengi değiştir"
          className="mt-1 h-3 w-3 shrink-0 rounded-full ring-2 ring-white"
          style={{ background: shop.isActive ? shop.color || "#6366f1" : "#d1d5db" }}
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-900">{shop.shopName || "(isimsiz mağaza)"}</span>
            {shop.isDefault && (
              <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                <Star className="h-2.5 w-2.5" /> Varsayılan
              </span>
            )}
            {!shop.isActive && (
              <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">Pasif</span>
            )}
            {isSelected && shop.isActive && (
              <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">Seçili</span>
            )}
            {shop.tokenExpired ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-amber-600">
                <AlertCircle className="h-3 w-3" /> Token süresi dolmuş
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600">
                <ShieldCheck className="h-3 w-3" /> Yetkili
              </span>
            )}
          </div>

          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
            <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-700">
              {PLATFORM_LABEL[shop.platform] || shop.platform}
            </span>
            <span>Shop ID: {shop.externalShopId}</span>
            <span>Key: {shop.apiKey || "-"}</span>
            {shop.shopUrl && (
              <a href={shop.shopUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-orange-600 hover:underline">
                Etsy <ExternalLink className="h-2.5 w-2.5" />
              </a>
            )}
          </div>

          {/* Dikey: bu magaza hangi urun grubunu satiyor.
              Yeni listede varsayilan urun tipini ve uretim mantigini belirler. */}
          <div className="mt-1.5 flex items-center gap-1.5">
            <label className="text-[11px] text-gray-500">Kategori:</label>
            <select
              value={shop.vertical}
              onChange={(e) => onPatch({ vertical: e.target.value })}
              className="rounded border border-gray-200 px-1.5 py-0.5 text-[11px] text-gray-700"
            >
              {VERTICALS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
            </select>
          </div>

          {/* Etiket (nis notu) */}
          <div className="mt-1">
            {editingLabel ? (
              <div className="flex items-center gap-1">
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="örnek: POD - Bebek"
                  className="rounded border border-gray-200 px-2 py-0.5 text-xs"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => { onPatch({ label }); setEditingLabel(false); }}
                  className="rounded bg-gray-900 px-2 py-0.5 text-[11px] text-white"
                >
                  Kaydet
                </button>
                <button
                  type="button"
                  onClick={() => { setLabel(shop.label || ""); setEditingLabel(false); }}
                  className="rounded border border-gray-200 px-2 py-0.5 text-[11px] text-gray-500"
                >
                  İptal
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setEditingLabel(true)}
                className="text-[11px] text-gray-400 hover:text-indigo-600"
              >
                {shop.label ? `Etiket: ${shop.label}` : "+ Etiket ekle"}
              </button>
            )}
          </div>

          {shop.lastError && (
            <p className="mt-1 line-clamp-2 text-[11px] text-red-600">{shop.lastError}</p>
          )}
        </div>

        {/* Aksiyonlar */}
        <div className="flex shrink-0 items-center gap-1">
          {busy && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}

          {shop.isActive && !isSelected && (
            <button
              type="button" onClick={onSelect}
              className="rounded-lg border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50"
            >
              Seç
            </button>
          )}
          {shop.isActive && !shop.isDefault && (
            <button
              type="button" onClick={() => onPatch({ isDefault: true })}
              title="Varsayılan mağaza yap"
              className="rounded-lg p-1.5 text-gray-400 hover:bg-amber-50 hover:text-amber-600"
            >
              <Star className="h-3.5 w-3.5" />
            </button>
          )}
          {/* IP sIkI mod: ilham gorselinden Yakin/Benzer kopya uretilmez (Dengeli'ye iner) */}
          <button
            type="button" onClick={() => onPatch({ ipStrict: !shop.ipStrict })} aria-pressed={!!shop.ipStrict}
            title={shop.ipStrict ? "IP sıkı mod açık: bu mağazada Yakın/Benzer kopya üretilmez (Dengeli). Kapatmak için tıkla" : "IP sıkı modu aç: Yakın/Benzer kopya üretimi kapanır"}
            className={clsx("rounded-lg border px-2 py-1 text-[11px] font-medium", shop.ipStrict ? "border-red-200 bg-red-50 text-red-700" : "border-gray-200 text-gray-500 hover:bg-gray-50")}
          >
            {shop.ipStrict ? "IP sıkı" : "IP normal"}
          </button>
          <button
            type="button" onClick={onRefresh}
            title="Token'ı tazele"
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          <button
            type="button" onClick={onReconnect}
            title="Yeniden bağla (Etsy yetkisini tazele)"
            className="rounded-lg p-1.5 text-gray-400 hover:bg-orange-50 hover:text-orange-600"
          >
            <Link2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onPatch({ isActive: !shop.isActive })}
            title={shop.isActive ? "Pasife al (token korunur)" : "Aktif et"}
            className={clsx(
              "rounded-lg p-1.5",
              shop.isActive ? "text-gray-400 hover:bg-gray-100 hover:text-gray-700" : "text-emerald-600 hover:bg-emerald-50"
            )}
          >
            <Power className="h-3.5 w-3.5" />
          </button>
          <button
            type="button" onClick={onDelete}
            title="Bağlantıyı kaldır"
            className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
